import React from 'react';
import { ClerkProvider } from '@clerk/react';
import { clerk, clerkPubKey } from './services/clerkConfig';

import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import './index.css';
import App from './App';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { CustomerioProvider } from './contexts/CustomerIOContext';
import { initMixpanel, IS_BOT_SESSION, Mixpanel } from './services/Mixpanel';
import { primeMixpanelTaggedFlags } from './services/FeatureFlagService';
import { getOrSetDiffitSessionId } from './utils/diffitSessionId';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'local',
  // Session Replay records every DOM mutation (on every session, for the on-error buffer)
  // and costs the export and screenshot renderers real time on a page nobody watches.
  integrations: [new Sentry.BrowserTracing(), ...(IS_BOT_SESSION ? [] : [new Sentry.Replay()])],
  tracesSampleRate: 0.05,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
  ignoreErrors: [
    // Browser extension RPC bridge errors (not from our code)
    /Object Not Found Matching Id/,
  ],
});

window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

// Initialize Mixpanel BEFORE the LaunchDarkly provider is created. Two reasons:
// the provider's initial context needs $device_id, which only exists once
// mixpanel.init() has run; and starting the fetch of the mixpanel-tagged flag
// list here puts it in flight during LD initialization rather than after React
// mounts. Both are synchronous, non-blocking calls.
initMixpanel();
primeMixpanelTaggedFlags();

// Built once, at module scope, and deliberately WITHOUT country_code.
//
// LaunchDarkly's localStorage bootstrap cache is keyed by a hash of the whole
// context, so any attribute that isn't stable across page loads silently defeats
// it. country_code is resolved by an async geo lookup that lands long after
// this line runs, so it is absent on a first-ever visit and present on the next
// one — two different hashes, meaning the bootstrap cache would never hit until
// the third visit. Leaving it off makes the hash identical from visit 1 onward.
//
// The cost is that a country-targeted flag evaluates without country for the
// ~1.4s before identify() runs; identify() supplies the full context including
// country_code, so this only narrows the pre-identify window, and on a
// first-ever visit the attribute genuinely isn't known yet anyway.
//
// sessionId and mpDeviceId are both durable (localStorage / cookie) and so are
// stable across loads. Nullish values are omitted rather than sent as null so a
// failed init can't change the hash either.
const initialLDContext = { kind: 'user', anonymous: true };
const ldSessionId = getOrSetDiffitSessionId();
const ldMpDeviceId = Mixpanel.getDeviceIdSync();
if (ldSessionId) initialLDContext.sessionId = ldSessionId;
if (ldMpDeviceId) initialLDContext.mpDeviceId = ldMpDeviceId;

const LDProvider = withLDProvider({
  clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_SIDE_ID,
  // Seed the initial anonymous context with the attributes flags target and
  // bucket on. Without this, LaunchDarkly evaluates the first render against a
  // bare auto-anonymous context — and a context missing the bucketBy attribute
  // hashes to bucket 0.0, so EVERY first paint is served the first variation of
  // the rollout instead of a random one. For a first-impression experiment that
  // isn't cosmetic: the treatment group never sees the treatment until
  // identify() resolves (median ~10s), and components that read flags during
  // that window (e.g. AppSidebar) render the wrong arm.
  //
  // No `key` is supplied: with anonymous: true the SDK generates and persists
  // one in localStorage, exactly as it does today.
  context: initialLDContext,
  options: {
    // Returning visitors render immediately with their last-known flag values
    // instead of `undefined`. Doesn't help a first-ever visit — closing that gap
    // needs a bucketing key the server already knows, which is deliberately out
    // of scope — but it's nearly free.
    bootstrap: 'localStorage',
  },
})(App);

// Activate the faces every pagination height is measured against, at startup.
//
// Preloading the file in index.html only fills the HTTP cache. A @font-face carrying a
// unicode-range is not *activated* until something paints a character in its range —
// which happens when packet content mounts, long after document.fonts.ready resolved
// against a near-empty page. Asking for the faces explicitly here loads them up front,
// so fonts.ready actually covers the font the measurement pass depends on instead of
// resolving before it and letting the load land mid-measurement.
//
// Inclusive Sans is a variable font: each subset is one @font-face spanning 300-700, so
// loading it at any weight activates every weight a packet paints with (400 body, 600
// and 700 headings). The two calls name the weights that matter; one would do.
if (typeof document !== 'undefined' && document.fonts?.load) {
  document.fonts.load('400 16px "Inclusive Sans"').catch(() => {});
  document.fonts.load('700 16px "Inclusive Sans"').catch(() => {});
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <ClerkProvider Clerk={clerk} publishableKey={clerkPubKey}>
    <React.StrictMode>
      <SpeedInsights />
      <CustomerioProvider siteId={import.meta.env.VITE_CUSTOMERIO_SITE_ID}>
        <LDProvider />
      </CustomerioProvider>
    </React.StrictMode>
  </ClerkProvider>
);
